// src/firebase/cloudinary.js
// ─────────────────────────────────────────────────────────────────────────────
// Cloudinary image upload & delete helpers
// Uses the unsigned upload preset — no billing account needed, 25 GB free
// ─────────────────────────────────────────────────────────────────────────────

const CLOUD_NAME    = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME;
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET;

// ─── Upload a file to Cloudinary ─────────────────────────────────────────────
// Returns the secure HTTPS URL of the uploaded image
export const uploadToCloudinary = async (file, folder = "general") => {
  const formData = new FormData();
  formData.append("file",           file);
  formData.append("upload_preset",  UPLOAD_PRESET);
  formData.append("folder",         `nilam-synthetics/${folder}`);
  // Auto-optimize: convert to WebP, limit max width to 1200px

  const res = await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`,
    { method: "POST", body: formData }
  );

  if (!res.ok) {
    const err = await res.json();
    throw new Error(err.error?.message || "Cloudinary upload failed");
  }

  const data = await res.json();
  return data.secure_url; // e.g. https://res.cloudinary.com/dplrarngk/image/upload/...
};

// ─── Delete an image from Cloudinary ─────────────────────────────────────────
// Extracts the public_id from the URL and calls the destroy endpoint
// Note: deletion via unsigned preset requires the public_id
export const deleteFromCloudinary = async (url) => {
  if (!url || !url.includes("cloudinary.com")) return;
  // Extract public_id from URL
  // URL format: https://res.cloudinary.com/CLOUD/image/upload/v123456/folder/filename.ext
  const parts    = url.split("/upload/");
  if (parts.length < 2) return;
  const withVersion = parts[1];                         // e.g. v123456/nilam-synthetics/designs/filename.jpg
  const publicId    = withVersion.replace(/^v\d+\//, "").replace(/\.[^.]+$/, ""); // strip version + extension

  const formData = new FormData();
  formData.append("public_id",     publicId);
  formData.append("upload_preset", UPLOAD_PRESET);

  await fetch(
    `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/destroy`,
    { method: "POST", body: formData }
  );
  // Errors are silently ignored — image may already be gone
};

/*
─────────────────────────────────────────────────────────────────────────────
HOW TO CREATE THE UPLOAD PRESET (one-time setup, 2 minutes)
─────────────────────────────────────────────────────────────────────────────

1. Go to https://cloudinary.com and log in
2. Top-right → Settings (gear icon) → Upload tab
3. Scroll down to "Upload presets" → Click "Add upload preset"
4. Set:
     Preset name:   nilam_synthetics       ← must match UPLOAD_PRESET above
     Signing mode:  Unsigned               ← IMPORTANT — allows browser uploads
     Folder:        nilam-synthetics       ← optional, keeps things organized
5. Click Save

That's it! Your app can now upload images directly from the browser.
─────────────────────────────────────────────────────────────────────────────
*/
